import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { AVATARS, CASES, SERVICES } from "../../data/content";
import { CtaButton } from "./Primitives";
import { scrollToId } from "./Nav";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  [{ t: "Your outsourced" }],
  [{ t: "revenue team", em: true }, { t: " —" }],
  [{ t: "without the cost of" }],
  [{ t: "building one in-house." }],
];

const MaskLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.35 + i * 0.11 }}
    >
      {parts.map((p) => (
        <span key={p.t} className={p.em ? "font-serif font-normal italic text-cobalt-700" : ""}>{p.t}</span>
      ))}
    </motion.span>
  </span>
);

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2, ease: EASE, delay: 0.7 }}
      className="relative [perspective:1400px]"
      data-testid="hero-visual"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="relative overflow-hidden rounded-[28px] border border-ink/10 bg-white p-3 shadow-[0_40px_100px_-40px_rgba(11,18,32,0.45)]">
          <div className="flex items-center justify-between px-3 pb-3 pt-1">
            <div className="flex gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-ink/15" /><span className="h-2.5 w-2.5 rounded-full bg-ink/15" /><span className="h-2.5 w-2.5 rounded-full bg-ink/15" /></div>
            <span className="text-[12px] font-medium text-ink/60">Live campaign · IT Services</span>
          </div>
          <div className="spotlight relative overflow-hidden rounded-[20px] bg-mist">
            <img src={CASES[1].img} alt="Coenzio outbound campaign dashboard" className="aspect-[1112/387] w-full object-cover object-left mix-blend-multiply" />
          </div>
          <div className="grid grid-cols-3 gap-2 pt-3">
            {[["13,762", "Sequences"], ["2.3%", "Reply rate"], ["$196K", "Pipeline"]].map(([v, l]) => (
              <div key={l} className="rounded-2xl bg-mist px-3 py-3">
                <div className="font-display text-xl font-bold text-ink sm:text-2xl">{v}</div>
                <div className="text-[12px] text-ink/60">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute -bottom-16 left-3 rounded-2xl border border-ink/10 bg-ink px-5 py-4 text-paper shadow-2xl sm:-left-10 [transform:translateZ(60px)]">
          <div className="text-[12px] uppercase tracking-[0.18em] text-cobalt-200">Meetings booked</div>
          <div className="font-display text-3xl font-bold">27 <span className="font-serif text-xl font-normal italic text-paper/80">in 6 days</span></div>
        </div>
        <div className="absolute -top-6 right-3 flex items-center gap-2 rounded-full border border-ink/10 bg-white py-2 pl-2 pr-4 shadow-xl sm:-right-8 [transform:translateZ(80px)]">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
          <span className="text-[13px] font-semibold text-ink">95/100 quality meetings</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGrid = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pb-24 pt-32 sm:pt-40 lg:pb-32">
      <motion.div style={{ y: yGrid }} className="hero-grid pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="mb-8 inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white/70 py-1.5 pl-2 pr-4 backdrop-blur" data-testid="hero-availability-badge">
            <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">Open</span>
            <span className="text-[13px] font-medium text-ink/75">Available for new projects</span>
          </motion.div>
          <h1 className="font-display text-[2.6rem] font-bold leading-[1.02] tracking-[-0.025em] text-ink sm:text-6xl lg:text-[4.6rem]" data-testid="hero-heading">
            {LINES.map((parts, i) => <MaskLine key={i} parts={parts} i={i} />)}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.95 }} className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
            We build and operate your outbound sales engine — from finding the right accounts to generating conversations and helping convert opportunities into customers.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.1 }} className="mt-10 flex flex-wrap items-center gap-4">
            <CtaButton testId="hero-primary-cta">Apply for Revenue Partnership</CtaButton>
            <button data-testid="hero-secondary-cta" onClick={() => scrollToId("results")} className="group inline-flex items-center gap-2 px-2 py-3 text-[15px] font-semibold text-ink">
              See the results <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
            </button>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.3 }} className="mt-12 flex items-center gap-4" data-testid="hero-trust">
            <div className="flex shrink-0 -space-x-3">
              {AVATARS.map((a) => <img key={a} src={a} alt="Coenzio client founder" className="h-11 w-11 rounded-full border-2 border-paper object-cover" />)}
            </div>
            <div className="text-[14px] leading-tight text-ink/70"><span className="block font-semibold text-ink">Trusted by 100+ founders</span>{SERVICES.slice(0, 3).join(" · ")}</div>
          </motion.div>
        </div>
        <motion.div style={{ y: yVisual }} className="lg:col-span-5">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
